import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Badge,
  Spinner,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FaFileInvoice, FaTruck, FaSearch, FaCheckCircle } from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import './DocumentSelectionModal.css';

const DocumentSelectionModal = ({
  isOpen,
  toggle,
  typeDocument,
  documents,
  loading,
  selectedDocuments,
  onDocumentsSelected
}) => {
  const { t } = useTranslation();
  const [localSelectedDocs, setLocalSelectedDocs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getTotalAmount = (document) =>
    document?.montantTotalTTC ?? document?.montantTTC ?? 0;

  const getPaidAmount = (document) =>
    document?.montantRegle ?? document?.montantPaye ?? 0;

  const getRemainingAmount = (document) =>
    Number(document?.montantRestantGlobal ?? document?.montantRestant
      ?? (getTotalAmount(document) - getPaidAmount(document)));

  const getCompanyLabel = (document) =>
    document?.company?.name ||
    document?.companyName ||
    document?.nomEntreprise ||
    '-';

  useEffect(() => {
    if (!selectedDocuments) return;

    setLocalSelectedDocs(prev => {
      const same =
        prev.length === selectedDocuments.length &&
        prev.every(p => selectedDocuments.some(s => s.id === p.id));

      return same ? prev : selectedDocuments;
    });
  }, [selectedDocuments]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const getFilteredDocuments = () => {
    if (!Array.isArray(documents)) return [];

    const searchLower = searchTerm.toLowerCase();

    return documents.filter(doc => {
      if (!searchLower) return true;

      return (
        doc.numero?.toLowerCase().includes(searchLower) ||
        doc.reference?.toLowerCase().includes(searchLower) ||
        doc.clientNom?.toLowerCase().includes(searchLower) ||
        getCompanyLabel(doc).toLowerCase().includes(searchLower)
      );
    });
  };

  const handleDocumentSelection = (document, isSelected) => {
    if (isSelected) {
      setLocalSelectedDocs(prev => [...prev, document]);
      return;
    }

    setLocalSelectedDocs(prev => prev.filter(doc => doc.id !== document.id));
  };

  const filteredDocuments = getFilteredDocuments();

  useEffect(() => {
    const selectableDocs = filteredDocuments.filter(doc => getRemainingAmount(doc) > 0);

    const selectedSelectableDocs = localSelectedDocs.filter(selected =>
      selectableDocs.some(doc => doc.id === selected.id)
    );

    setSelectAll(
      selectedSelectableDocs.length > 0 &&
      selectableDocs.length > 0 &&
      selectedSelectableDocs.length === selectableDocs.length
    );
  }, [filteredDocuments, localSelectedDocs, getRemainingAmount]);

  const handleSelectAll = () => {
    const selectableDocs = filteredDocuments.filter(doc => getRemainingAmount(doc) > 0);

    if (selectAll) {
      setLocalSelectedDocs(prev =>
        prev.filter(selected => !selectableDocs.some(doc => doc.id === selected.id))
      );
      return;
    }

    const newSelections = selectableDocs.filter(
      doc => !localSelectedDocs.some(sel => sel.id === doc.id)
    );
    setLocalSelectedDocs(prev => [...prev, ...newSelections]);
  };

  const confirmSelection = () => {
    onDocumentsSelected?.(localSelectedDocs);
    toggle?.();
  };

  const calculateTotalAmount = () =>
    localSelectedDocs.reduce((total, doc) => total + getRemainingAmount(doc), 0);

  const statistics = {
    total: filteredDocuments.length,
    selected: localSelectedDocs.length,
    selectedAmount: calculateTotalAmount(),
    unpaidAmount: filteredDocuments.reduce((sum, doc) => sum + getRemainingAmount(doc), 0)
  };

  const getStatusBadge = (document) => {
    if (typeDocument === 'FACTURE') {
      switch (document.statut) {
        case 'BROUILLON':
          return <Badge color="secondary">{t('facture.brouillon')}</Badge>;
        case 'VALIDEE':
          return <Badge color="primary">{t('facture.validee')}</Badge>;
        case 'PARTIELLEMENT_PAYEE':
          return <Badge color="warning">{t('facture.partiellementPayee')}</Badge>;
        case 'PAYEE':
          return <Badge color="success">{t('facture.payee')}</Badge>;
        case 'EN_RETARD':
          return <Badge color="danger">{t('facture.enRetard')}</Badge>;
        default:
          return <Badge color="secondary">{document.statut}</Badge>;
      }
    }

    switch (document.status) {
      case 'EN_PREPARATION':
        return <Badge color="warning">{t('delivery.enpreparation')}</Badge>;
      case 'EN_COURS':
        return <Badge color="secondary">{t('delivery.encours')}</Badge>;
      case 'LIVRE':
        return <Badge color="success">{t('delivery.livre')}</Badge>;
      case 'RETOUR':
        return <Badge color="danger">{t('delivery.retourne')}</Badge>;
      default:
        return <Badge color="secondary">{document.status}</Badge>;
    }
  };

  const getPaymentStatusBadge = (document) => {
    const montantPaye = getPaidAmount(document);
    const montantTotal = getTotalAmount(document);

    if (montantPaye === 0) {
      return <Badge color="danger">{t('reglement.impaye')}</Badge>;
    }

    if (montantPaye < montantTotal) {
      return <Badge color="warning">{t('reglement.partiel')}</Badge>;
    }

    return <Badge color="success">{t('reglement.paye')}</Badge>;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        {typeDocument === 'FACTURE'
          ? t('reglement.factures')
          : t('reglement.bonLivraisons')}
      </ModalHeader>
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="text-muted">
            {statistics.total} {t('reglement.documents', 'documents')} - {t('reglement.total')}: {formatPrice(statistics.unpaidAmount, false)} DH
          </div>
          <InputGroup style={{ maxWidth: '320px' }}>
            <InputGroupText>
              <FaSearch />
            </InputGroupText>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('common.search', 'Rechercher')}
            />
          </InputGroup>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted">{t('common.loading')}</div>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead className="table-light sticky-top">
                <tr>
                  <th width="50">
                    <Input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={filteredDocuments.filter(doc => getRemainingAmount(doc) > 0).length === 0}
                    />
                  </th>
                  <th>{t('reglement.numero')}</th>
                  <th>{t('reglement.client')}</th>
                  <th>{t('company.company', 'Entreprise')}</th>
                  <th>{t('reglement.date')}</th>
                  <th>{t('reglement.montantTTC')}</th>
                  <th>{t('reglement.montantPaye')}</th>
                  <th>{t('reglement.montantRestant')}</th>
                  {typeDocument === 'FACTURE' && <th>{t('reglement.dateEcheance')}</th>}
                  <th>{t('reglement.statut')}</th>
                  <th>{t('reglement.paiement')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={typeDocument === 'FACTURE' ? '11' : '10'} className="text-center text-muted py-4">
                      {searchTerm ? (
                        <>
                          <FaSearch className="mb-2" />
                          <div>{t('reglement.noDocumentsFound')}</div>
                        </>
                      ) : (
                        <>
                          {typeDocument === 'FACTURE' ? (
                            <FaFileInvoice className="mb-2" />
                          ) : (
                            <FaTruck className="mb-2" />
                          )}
                          <div>
                            {typeDocument === 'FACTURE'
                              ? t('reglement.noFacturesImpayees')
                              : t('reglement.noBonLivraisonsNonRegles')}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((document) => (
                    <tr
                      key={document.id}
                      className={localSelectedDocs.some(doc => doc.id === document.id) ? 'table-active' : ''}
                    >
                      <td>
                        {getRemainingAmount(document) > 0 ? (
                          <Input
                            type="checkbox"
                            checked={localSelectedDocs.some(doc => doc.id === document.id)}
                            onChange={(e) => handleDocumentSelection(document, e.target.checked)}
                          />
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <strong>{document.numero}</strong>
                      </td>
                      <td>{document.clientNom || 'N/A'}</td>
                      <td>{getCompanyLabel(document)}</td>
                      <td>{document.createdAt || document.dateFacture}</td>
                      <td>
                        <strong>{formatPrice(getTotalAmount(document), false)} DH</strong>
                      </td>
                      <td>{formatPrice(getPaidAmount(document), false)} DH</td>
                      <td className="fw-bold text-danger">
                        {formatPrice(getRemainingAmount(document), false)} DH
                      </td>
                      {typeDocument === 'FACTURE' && (
                        <td>
                          {document.dateEcheance
                            ? new Date(document.dateEcheance).toLocaleDateString('fr-FR')
                            : '-'}
                        </td>
                      )}
                      <td>{getStatusBadge(document)}</td>
                      <td>{getPaymentStatusBadge(document)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <div className="me-auto text-muted">
          {statistics.selected} {t('reglement.documentsSelectionnes')} - {formatPrice(statistics.selectedAmount, false)} DH
        </div>
        <Button color="secondary" onClick={toggle}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button color="success" onClick={confirmSelection}>
          <FaCheckCircle className="me-1" />
          {t('reglement.confirmerSelection')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DocumentSelectionModal;
